import { useEffect, useMemo, useState } from 'react';
import type { BunkerClientMessage, BunkerPrivateState, BunkerPlayerPublic, BunkerSpecialCard } from '@shum/shared';
import { actionId } from '../../lib/bunkerSocket';
import { formatRemaining, useCountdown } from '../../lib/format';

interface BunkerScreensProps {
  privateState: BunkerPrivateState | null;
  send: (message: BunkerClientMessage) => boolean;
}

export function BunkerScreens({ privateState, send }: BunkerScreensProps) {
  if (!privateState) return <Waiting title="Подключаемся к бункеру" text="Проверяем защищённую игровую сессию…" />;

  switch (privateState.screen) {
    case 'intro':
      return <Intro state={privateState} />;
    case 'character_review':
      return <CharacterReview state={privateState} />;
    case 'reveal_selection':
      return <RevealSelection state={privateState} send={send} />;
    case 'player_speech':
      return <Speech state={privateState} send={send} />;
    case 'discussion':
      return <Discussion state={privateState} />;
    case 'action_window':
      return <ActionWindow state={privateState} send={send} />;
    case 'voting':
      return <Voting state={privateState} send={send} />;
    case 'vote_results':
      return <VoteResults state={privateState} />;
    case 'tie_discussion':
      return <TieDiscussion state={privateState} />;
    case 'elimination':
      return <Elimination state={privateState} />;
    case 'results':
      return <Results state={privateState} />;
    default:
      return <Waiting title={phaseText(privateState.phase)} text={privateState.paused ? 'Матч поставлен на паузу ведущим.' : 'Системный ведущий готовит следующий этап.'} state={privateState} />;
  }
}

function Timer({ endsAt }: { endsAt: string | null }) {
  const seconds = useCountdown(endsAt);
  return <span className={`bk-timer ${seconds <= 10 ? 'urgent' : ''}`}>{formatRemaining(seconds)}</span>;
}

function Intro({ state }: { state: BunkerPrivateState }) {
  const scenario = state.scenario;
  const bunker = state.bunker;
  return (
    <section className="bk-stage bk-intro">
      <span className="bk-eyebrow">Катастрофа · выживших {scenario?.survivalRate ?? '—'}</span>
      <h1>{scenario?.title ?? 'Неизвестная катастрофа'}</h1>
      <p className="bk-lede">{scenario?.fullDescription}</p>
      <div className="bk-intro-grid">
        <article className="bk-info-card">
          <span>Изоляция</span>
          <strong>{scenario?.isolation}</strong>
          <small>до безопасного выхода</small>
        </article>
        <article className="bk-info-card">
          <span>Бункер</span>
          <strong>{bunker?.title}</strong>
          <small>{bunker?.area} · автономность {bunker?.autonomy}</small>
        </article>
      </div>
      <div className="bk-feature-strip">
        {(bunker?.revealedFeatures ?? []).map((feature) => <span key={feature}>{feature}</span>)}
      </div>
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function CharacterReview({ state }: { state: BunkerPrivateState }) {
  const [concealed, setConcealed] = useState(false);
  const character = state.character;
  if (concealed) {
    return (
      <section className="bk-stage">
        <span className="bk-eyebrow">Личная карточка скрыта</span>
        <h1>Никто не увидит ваши данные</h1>
        <button className="bk-cta" onClick={() => setConcealed(false)}>Показать карточку</button>
        <Timer endsAt={state.endsAt} />
      </section>
    );
  }
  return (
    <section className="bk-review">
      <div className="bk-section-head">
        <div>
          <span className="bk-eyebrow">Только для вас</span>
          <h1>Ваш персонаж</h1>
          <p>Запомните сильные стороны и подготовьте аргументы. Всё ниже хранится на сервере и пока скрыто от остальных.</p>
        </div>
        <button className="bk-link-btn" onClick={() => setConcealed(true)}>Скрыть экран</button>
      </div>
      <div className="bk-bio-row">
        <span>{character?.age ?? '—'} лет</span>
        <span>{character?.biology ?? '—'}</span>
      </div>
      <div className="bk-character-grid private">
        {character?.fields.map((field) => (
          <article className={`bk-character-field ${field.status === 'revealed' ? 'revealed' : ''}`} key={field.type}>
            <span>{field.label}</span>
            <strong>{field.value}</strong>
            {field.detail && <small>{field.detail}</small>}
            <em>{field.status === 'revealed' ? 'уже раскрыто' : 'скрыто'}</em>
          </article>
        ))}
      </div>
      {state.specialCards.length > 0 && (
        <div className="bk-cards-preview">
          <span className="bk-eyebrow">Ваши специальные карты</span>
          {state.specialCards.map((card) => <span className="bk-card-chip" key={card.instanceId}>{card.title}</span>)}
        </div>
      )}
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function RevealSelection({ state, send }: { state: BunkerPrivateState; send: (message: BunkerClientMessage) => boolean }) {
  if (!state.canSelectReveal) {
    return (
      <section className="bk-stage">
        <span className="bk-eyebrow">Раунд {state.roundNumber} · ход {state.currentTurnIndex + 1}/{state.currentTurnTotal}</span>
        <h1>{state.currentPlayerName ?? 'Игрок'} выбирает характеристику</h1>
        <p>Скоро он раскроет один факт о своём персонаже.</p>
        <PlayerWall players={state.players} compact />
        <Timer endsAt={state.endsAt} />
      </section>
    );
  }
  const fields = state.character?.fields.filter((field) => state.selectableFields.includes(field.type)) ?? [];
  return (
    <section className="bk-stage bk-select">
      <span className="bk-eyebrow">Ваш ход · раунд {state.roundNumber}</span>
      <h1>Что раскроете?</h1>
      <p>Выбор необратим. Объясните группе, почему это поможет выжить.</p>
      <div className="bk-choice-grid">
        {fields.map((field) => (
          <button
            className="bk-choice"
            key={field.type}
            onClick={() => send({ type: 'bunker.select_reveal', fieldType: field.type, actionId: actionId() })}
          >
            <span>{field.label}</span>
            <strong>{field.value}</strong>
            {field.detail && <small>{field.detail}</small>}
          </button>
        ))}
      </div>
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function Speech({ state, send }: { state: BunkerPrivateState; send: (message: BunkerClientMessage) => boolean }) {
  const current = state.players.find((player) => player.playerId === state.currentPlayerId);
  const canEnd = state.currentPlayerId !== null && state.currentPlayerId === ownId(state);
  return (
    <section className={`bk-stage bk-speech ${state.currentPlayerId === ownId(state) ? 'your-turn' : ''}`}>
      <span className="bk-eyebrow">Выступление · {state.currentTurnIndex + 1}/{state.currentTurnTotal}</span>
      <h1>{state.currentPlayerId === ownId(state) ? 'Говорите' : `${state.currentPlayerName ?? 'Игрок'} говорит`}</h1>
      {current && <PublicPlayerCard player={current} prominent />}
      <p>{state.currentPlayerId === ownId(state) ? 'Объясните, чем эта характеристика полезна группе.' : 'Слушайте аргументы и готовьте вопросы.'}</p>
      <Timer endsAt={state.endsAt} />
      {canEnd && <button className="bk-cta" onClick={() => send({ type: 'bunker.end_speech', actionId: actionId() })}>Я всё →</button>}
    </section>
  );
}

function Discussion({ state }: { state: BunkerPrivateState }) {
  return (
    <section className="bk-discussion">
      <div className="bk-section-head inline">
        <div>
          <span className="bk-eyebrow">Раунд {state.roundNumber} · общее обсуждение</span>
          <h1>Кого берём в бункер?</h1>
          <p>Обсудите раскрытые данные. После таймера начнётся окно специальных карт.</p>
        </div>
        <Timer endsAt={state.endsAt} />
      </div>
      <PlayerWall players={state.players} />
    </section>
  );
}

function ActionWindow({ state, send }: { state: BunkerPrivateState; send: (message: BunkerClientMessage) => boolean }) {
  const activeTargets = useMemo(() => state.players.filter((player) => player.isActive && player.playerId !== ownId(state)), [state.players, state]);
  const [targetId, setTargetId] = useState(activeTargets[0]?.playerId ?? '');
  useEffect(() => {
    if (!activeTargets.some((target) => target.playerId === targetId)) setTargetId(activeTargets[0]?.playerId ?? '');
  }, [activeTargets, targetId]);
  return (
    <section className="bk-action-window">
      <div className="bk-section-head inline">
        <div>
          <span className="bk-eyebrow">Перед голосованием</span>
          <h1>Специальные карты</h1>
          <p>Карты обрабатывает сервер по приоритету. Неиспользованные останутся у вас.</p>
        </div>
        <Timer endsAt={state.endsAt} />
      </div>
      {state.specialCards.filter((card) => card.status === 'available').length === 0 ? (
        <div className="bk-empty">У вас нет доступных карт. Следите за картами других игроков.</div>
      ) : (
        <>
          <label className="bk-target-select">
            <span>Цель для направленной карты</span>
            <select value={targetId} onChange={(event) => setTargetId(event.target.value)}>
              {activeTargets.map((target) => <option key={target.playerId} value={target.playerId}>{target.name}</option>)}
            </select>
          </label>
          <div className="bk-card-grid">
            {state.specialCards.map((card) => <SpecialCard key={card.instanceId} card={card} targetId={targetId} send={send} />)}
          </div>
        </>
      )}
      <PlayerWall players={state.players} compact />
    </section>
  );
}

function SpecialCard({ card, targetId, send }: { card: BunkerSpecialCard; targetId: string; send: (message: BunkerClientMessage) => boolean }) {
  const needsTarget = card.type === 'force_reveal' || card.type === 'swap_baggage';
  return (
    <article className={`bk-special-card ${card.status !== 'available' ? 'used' : ''}`}>
      <span>Приоритет {card.priority}</span>
      <h3>{card.title}</h3>
      <p>{card.description}</p>
      <button
        className="bk-small-cta"
        disabled={card.status !== 'available' || (needsTarget && !targetId)}
        onClick={() => send({ type: 'bunker.play_card', cardInstanceId: card.instanceId, targetPlayerId: needsTarget ? targetId : undefined, actionId: actionId() })}
      >
        {card.status === 'available' ? 'Использовать' : 'Использована'}
      </button>
    </article>
  );
}

function Voting({ state, send }: { state: BunkerPrivateState; send: (message: BunkerClientMessage) => boolean }) {
  const voted = Boolean(state.selectedVoteId);
  const revote = state.phase === 'REVOTE';
  return (
    <section className="bk-voting">
      <div className="bk-section-head inline">
        <div>
          <span className="bk-eyebrow">{revote ? 'Переголосование' : 'Тайное голосование'}</span>
          <h1>{revote ? 'Выберите одного из лидеров' : 'Кого не берём в бункер?'}</h1>
          <p>{voted ? 'Голос принят сервером. Результаты откроются одновременно для всех.' : 'После подтверждения голос изменить нельзя.'}</p>
        </div>
        <div className="bk-vote-meta"><Timer endsAt={state.endsAt} /><span>{state.votingSubmittedCount}/{state.votingPlayerCount} голосов</span></div>
      </div>
      <div className="bk-vote-grid">
        {state.voteCandidates.map((candidate) => {
          const player = state.players.find((item) => item.playerId === candidate.playerId);
          return (
            <button
              key={candidate.playerId}
              className={`bk-vote-card ${state.selectedVoteId === candidate.playerId ? 'selected' : ''}`}
              disabled={voted}
              onClick={() => send({ type: 'bunker.vote', targetPlayerId: candidate.playerId, actionId: actionId() })}
            >
              <span>{player?.avatarId ?? '◌'} {candidate.name}</span>
              <small>{player?.revealedFields.map((field) => field.value).join(' · ') || 'Характеристики пока скрыты'}</small>
              {state.selectedVoteId === candidate.playerId && <em>Голос принят</em>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function VoteResults({ state }: { state: BunkerPrivateState }) {
  return (
    <section className="bk-stage">
      <span className="bk-eyebrow">Голосование завершено</span>
      <h1>Результаты</h1>
      <VoteTotals totals={state.voteTotals} />
      <p>Следом системный ведущий объявит исключение.</p>
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function TieDiscussion({ state }: { state: BunkerPrivateState }) {
  return (
    <section className="bk-stage">
      <span className="bk-eyebrow">Ничья</span>
      <h1>Нужен ещё один выбор</h1>
      <div className="bk-tie-list">{state.tieCandidates.map((candidate) => <span key={candidate.playerId}>{candidate.name}</span>)}</div>
      <p>Обсудите лидеров. Затем голосование будет доступно только между ними.</p>
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function Elimination({ state }: { state: BunkerPrivateState }) {
  const eliminated = state.eliminatedPlayer;
  return (
    <section className="bk-stage bk-elimination">
      <span className="bk-eyebrow">Решение группы</span>
      <h1>{eliminated ? `${eliminated.name} не попадает в бункер` : 'Исключение'}</h1>
      <VoteTotals totals={state.voteTotals} />
      <p>Игрок остаётся наблюдателем и больше не участвует в голосовании.</p>
      <Timer endsAt={state.endsAt} />
    </section>
  );
}

function Results({ state }: { state: BunkerPrivateState }) {
  return (
    <section className="bk-results">
      <span className="bk-eyebrow">Финальный состав бункера</span>
      <h1>Вы выбрали выживших</h1>
      <div className="bk-final-grid">
        {state.finalCharacters.map((entry) => (
          <article className="bk-final-card" key={entry.playerId}>
            <h2>{entry.name}</h2>
            <span>{entry.character.age} лет · {entry.character.biology}</span>
            <div>{entry.character.fields.map((field) => <p key={field.type}><b>{field.label}:</b> {field.value}</p>)}</div>
          </article>
        ))}
      </div>
      {state.survival && (
        <article className={`bk-survival ${state.survival.outcome}`}>
          <div><span>Симуляция выживания</span><strong>{state.survival.score}/100</strong></div>
          <h2>{state.survival.title}</h2>
          <p>{state.survival.summary}</p>
          <ul>{state.survival.story.map((line) => <li key={line}>{line}</li>)}</ul>
        </article>
      )}
    </section>
  );
}

function VoteTotals({ totals }: { totals: BunkerPrivateState['voteTotals'] }) {
  if (totals.length === 0) return <p className="bk-empty">Голоса не поступили.</p>;
  return <div className="bk-totals">{totals.map((total) => <div key={total.playerId}><span>{total.name}</span><strong>{total.votes}</strong></div>)}</div>;
}

function PlayerWall({ players, compact = false }: { players: BunkerPlayerPublic[]; compact?: boolean }) {
  return <div className={`bk-player-wall ${compact ? 'compact' : ''}`}>{players.map((player) => <PublicPlayerCard key={player.playerId} player={player} />)}</div>;
}

function PublicPlayerCard({ player, prominent = false }: { player: BunkerPlayerPublic; prominent?: boolean }) {
  return (
    <article className={`bk-public-player ${prominent ? 'prominent' : ''} ${player.isEliminated ? 'eliminated' : ''}`}>
      <div className="bk-player-name"><span>{player.avatarId}</span><strong>{player.name}</strong>{player.isCreator && <em>ведущий</em>}</div>
      <div className="bk-public-fields">
        {player.revealedFields.length > 0 ? player.revealedFields.map((field) => <span key={field.type}><b>{field.label}</b> {field.value}</span>) : <span className="unknown">характеристики скрыты</span>}
      </div>
      {player.hasImmunity && <small className="bk-effect">иммунитет</small>}
    </article>
  );
}

function Waiting({ title, text, state }: { title: string; text: string; state?: BunkerPrivateState }) {
  return (
    <section className="bk-stage">
      <span className="bk-eyebrow">{state ? phaseText(state.phase) : 'Бункер'}</span>
      <h1>{title}</h1>
      <p>{text}</p>
      {state?.endsAt && <Timer endsAt={state.endsAt} />}
    </section>
  );
}

function ownId(state: BunkerPrivateState): string | null {
  return state.viewerPlayerId;
}

function phaseText(phase: BunkerPrivateState['phase']): string {
  const map: Record<string, string> = {
    LOBBY: 'Лобби', INTRO: 'Катастрофа', CHARACTER_REVIEW: 'Ваш персонаж', ROUND_START: 'Новый раунд', REVEAL_SELECTION: 'Выбор характеристики', PLAYER_REVEAL: 'Раскрытие', PLAYER_SPEECH: 'Выступление', GENERAL_DISCUSSION: 'Обсуждение', ACTION_WINDOW: 'Специальные карты', VOTING: 'Голосование', VOTE_RESULTS: 'Результаты', TIE_DISCUSSION: 'Ничья', REVOTE: 'Переголосование', ELIMINATION: 'Исключение', RESULTS: 'Итоги', PAUSED: 'Пауза',
  };
  return map[phase] ?? phase;
}
